import React, { useState, useEffect, useRef } from "react";
import { useSelector } from 'react-redux';
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import axios from "axios";
import Swal from "sweetalert2";
import { translator } from '../../utils/Translator';

const PRODUCT_FETCH_TIMEOUT_MS = 15000;

const ProductList = ({ onSelect, selectedProduct = null, filterData = null, isDisabled = false, docType = null, customer = null ,salesman = null }) => {
  const { translate } = useSelector(({ i18n }) => i18n);
  const { authUser } = useSelector(({ auth }) => auth);
  const { languageSelected } = useSelector(({ settings }) => settings);

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const itemRefs = useRef([]);

  console.log("filterData", filterData);


  // Fetch products from API with filter
  const fetchProducts = async () => {
    setLoading(true);

    const params = {
      company: authUser.company !== "#" ? authUser.company : "",
      salesmanCode: salesman?.SalesmanCode || "",
      customerCode: customer?.CustomerCode || "",
      language: languageSelected.id,
      currencyCode: "THB",
      docType: docType,
      // เพิ่ม filter params
      ...(filterData?.itemGroup && { itemGroup: filterData.itemGroup }),
      ...(filterData?.productGroup && { productGroup: filterData.productGroup }),
      ...(filterData?.productLine && { productLine: filterData.productLine }),
      ...(filterData?.productType && { productType: filterData.productType }),
      ...(filterData?.mktEventCode && { mktEventCode: filterData.mktEventCode }),
      ...(filterData?.productSetCode && { productSetCode: filterData.productSetCode }),
      ...(filterData?.vatType && { vatType: filterData.vatType }),
      ...(filterData?.selectedTab === "frequent" && { isFavorite: true }),
    };

    const url = `${process.env.REACT_APP_API_URL}product`;
    const config = {
      params,
      timeout: PRODUCT_FETCH_TIMEOUT_MS,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('x-access-token')}`,
      },
    };

    try {
      const response = await axios.get(url, config);

      if (response.data && response.data.noSalesmanItemGroup) {
        setProducts([]);
        Swal.fire({
          icon: "warning",
          title: translator(translate.ISA_Warning, "แจ้งเตือน"),
          text: translator(
            translate.ISA_PleaseAssignProductToSalesman,
            "พนักงานขายคนนี้ยังไม่ถูกกำหนดกลุ่มสินค้า กรุณากำหนดสินค้าให้กับพนักงานก่อน",
          ),
        });
        return;
      }

      const productData = response.data.data || response.data;
      setProducts(productData);
      console.log("Products loaded:", response.data);

      // Auto-select first product if available (เฉพาะเมื่อไม่มี selectedProduct)
      if (productData && productData.length > 0 && onSelect && !selectedProduct) {
        setSelectedIndex(0);
        onSelect(productData[0]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);

      if (error.code === "ECONNABORTED") {
        Swal.fire({
          icon: "error",
          title: translator(translate.ISA_Warning, "แจ้งเตือน"),
          text: translator(
            translate.ISA_ProductLoadTimeout,
            "โหลดรายการสินค้านานเกินไป กรุณาลองใหม่อีกครั้ง",
          ),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch products เมื่อ component mount
  useEffect(() => {
    if (authUser && authUser.company) {
      fetchProducts();
    }
  }, [authUser, languageSelected]);

  // Fetch products ใหม่เมื่อ filter เปลี่ยน
  useEffect(() => {
    if (authUser && authUser.company && filterData) {
      fetchProducts();
    }
  }, [filterData]);

  const handleSelect = (product, index) => {
    if (isDisabled) return; // ป้องกันการเลือกสินค้าใน disabled mode
    setSelectedIndex(index);
    if (onSelect) {
      onSelect(product); // 🔹 ส่งค่ากลับไปที่ Parent
    }
  };

  // Filter products based on search keyword
  const filteredProducts = products.filter((item) => {
    if (!searchKeyword) return true;
    const keyword = searchKeyword.toLowerCase();
    return (
      item.ProductName?.toLowerCase().includes(keyword) ||
      item.ProductCode?.toLowerCase().includes(keyword) ||
      item.ProductGroup?.toLowerCase().includes(keyword)
    );
  });

  // จำกัดการแสดงผลเพียง 200 รายการแรก
  const displayProducts = filteredProducts.slice(0, 200);

  // useEffect สำหรับตั้งค่า selectedIndex เมื่อมี selectedProduct (สำหรับ edit mode)
  useEffect(() => {
    if (selectedProduct && displayProducts.length > 0) {
      // หา index ของสินค้าที่ตรงกับ selectedProduct ใน display products
      const productIndex = displayProducts.findIndex(
        (product) => product.ProductCode === selectedProduct.ProductCode
      );

      if (productIndex !== -1) {
        setSelectedIndex(productIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct, products.length, searchKeyword]);

  // Clear product selection when search returns no results
  useEffect(() => {
    if (displayProducts.length === 0 && searchKeyword.trim() !== "") {
      if (onSelect) {
        onSelect(null);
      }
      setSelectedIndex(null);
    }
  }, [displayProducts.length, searchKeyword, onSelect]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {translator(translate.ISA_AllProductListTitle, "รายการสินค้าทั้งหมด")} - {filteredProducts.length.toLocaleString()} {translator(translate.ISA_Items, "รายการ")}
        {filteredProducts.length > 200 && (
          <Typography variant="caption" display="block" color="textSecondary">
            {translator(translate.ISA_ShowingFirst200, "(แสดง 200 รายการแรก)")}
          </Typography>
        )}
      </Typography>

      <TextField
        fullWidth
        variant="outlined"
        placeholder={translator(translate.ISA_SearchProductPlaceholder, "ค้นหาสินค้า (ชื่อสินค้า, รหัสสินค้า, กลุ่มสินค้า)")}
        value={searchKeyword}
        onChange={(e) => setSearchKeyword(e.target.value)}
        disabled={isDisabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        style={{ marginBottom: 16 }}
      />

      {loading ? (
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          style={{
            maxHeight: 600,
            overflowY: "auto",
            padding: 8,
          }}
        >
          <List>
            {displayProducts.length === 0 ? (
              <Box
                p={3}
                textAlign="center"
                border="2px dashed #ccc"
                borderRadius={2}
                bgcolor="#fff9e6"
              >
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  {translator(translate.ISA_NoProductFound, "ไม่พบข้อมูลสินค้า")}
                </Typography>
                {searchKeyword && (
                  <Typography variant="body2" color="textSecondary">
                    {translator(translate.ISA_TrySearchAgain, "ลองค้นหาด้วยคำอื่นหรือตรวจสอบการสะกด")}
                  </Typography>
                )}
              </Box>
            ) : (
              displayProducts.map((item, index) => (
              <ListItem
                key={index}
                ref={(el) => (itemRefs.current[index] = el)}
                button
                onClick={() => handleSelect(item, index)}
                disabled={isDisabled}
                style={{
                  marginBottom: 8,
                  borderRadius: 12,
                  boxShadow:
                    selectedIndex === index
                      ? "0px 4px 10px rgba(255, 100, 0, 0.3)"
                      : "0px 2px 6px rgba(0,0,0,0.1)",
                  backgroundColor:
                    selectedIndex === index ? "#fff3e6" : "white",
                  border:
                    selectedIndex === index
                      ? "2px solid #ff6600"
                      : "1px solid #eee",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.6 : 1,
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={item.Image}
                    alt={item.ProductName}
                    style={{ width: 56, height: 56, borderRadius: 8, marginRight: 8 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={item.ProductName}
                  secondary={
                    <>
                      <Typography
                        variant="body2"
                        style={{ color: "#999" }}
                      >
                        {item.ProductCode}{" "}
                        {item.ProductGroup && (
                          <span style={{ color: "#aaa" }}>
                            – ({item.ProductGroup})
                          </span>
                        )}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))
            )}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default ProductList;